import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { IMediaTranscoderRepository } from '../../../../../domain/media-transcoder/media-transcoder.repository';
import {
  DEFAULT_SHAKA_PACKAGER_PACKAGING_MODE,
  EShakaPackagerPackagingMode,
  existingModeLabel,
  isFullPackagingMode,
  packagingModeLabel,
  ShakaPackagerEligibleMedia,
} from '../../../../../domain/media-transcoder/shaka-packager-eligible-media';

const MODAL_ID = 'packagerBulkCreateModal';

export interface PackagerEligibleMediaRow extends ShakaPackagerEligibleMedia {
  selected: boolean;
  mode: EShakaPackagerPackagingMode;
}

@Component({
  selector: 'evrideo-packager-bulk-create-dialog',
  templateUrl: './packager-bulk-create-dialog.component.html',
  styleUrls: ['./packager-bulk-create-dialog.component.scss'],
})
export class PackagerBulkCreateDialogComponent implements OnInit {
  @Output() completed = new EventEmitter<void>();

  readonly packagingModes = EShakaPackagerPackagingMode;
  readonly packagingModeLabel = packagingModeLabel;
  readonly existingModeLabel = existingModeLabel;

  rows: PackagerEligibleMediaRow[] = [];
  isLoading = false;
  isApplying = false;

  nameFilter = '';
  transcodingStartedAfterFilter = '';
  packagerScheduledAfterFilter = '';
  rangeFrom = 1;
  rangeTo = 1;
  rescheduleExisting = false;
  setAllMode: EShakaPackagerPackagingMode =
    DEFAULT_SHAKA_PACKAGER_PACKAGING_MODE;

  constructor(
    private _ngxSmartModalService: NgxSmartModalService,
    private _mediaTranscoderRepository: IMediaTranscoderRepository,
    private _toastr: ToastrService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this._ngxSmartModalService.getModal(MODAL_ID).onOpen.subscribe(() => {
      this._ngZone.run(() => {
        void this._loadEligibleMedia();
      });
    });
  }

  get filteredRows(): PackagerEligibleMediaRow[] {
    let result = this.rows;

    const nameQuery = this.nameFilter.trim().toLowerCase();
    if (nameQuery) {
      result = result.filter(
        (row) =>
          row.mediaName.toLowerCase().includes(nameQuery) ||
          String(row.mediaId).includes(nameQuery),
      );
    }

    if (this.transcodingStartedAfterFilter) {
      const cutoff = this._parseFilterCutoff(this.transcodingStartedAfterFilter);
      if (cutoff) {
        result = result.filter((row) =>
          row.earliestStartTime.isSameOrAfter(cutoff),
        );
      }
    }

    if (this.packagerScheduledAfterFilter) {
      const cutoff = this._parseFilterCutoff(this.packagerScheduledAfterFilter);
      if (cutoff) {
        result = result.filter((row) => {
          const scheduledAt = this.packagerScheduledAt(row);
          return (
            row.existingCount > 0 &&
            scheduledAt != null &&
            scheduledAt.isSameOrAfter(cutoff)
          );
        });
      }
    }

    return result;
  }

  get selectedRows(): PackagerEligibleMediaRow[] {
    return this.rows.filter((row) => row.selected);
  }

  get selectedCount(): number {
    return this.selectedRows.length;
  }

  get metadataOnlySelectedCount(): number {
    return this.selectedRows.filter(
      (row) => row.mode === EShakaPackagerPackagingMode.MetadataOnly,
    ).length;
  }

  get allFilteredSelected(): boolean {
    const visible = this.filteredRows;
    return visible.length > 0 && visible.every((row) => row.selected);
  }

  cancel(): void {
    if (this.isApplying) {
      return;
    }
    this._ngxSmartModalService.getModal(MODAL_ID).close();
  }

  toggleSelectAll(): void {
    const select = !this.allFilteredSelected;
    const visibleIds = new Set(this.filteredRows.map((row) => row.mediaId));
    this.rows.forEach((row) => {
      if (visibleIds.has(row.mediaId)) {
        row.selected = select && this._canSelectRow(row);
      }
    });
    this._refreshView();
  }

  applyRangeSelection(): void {
    const visible = this.filteredRows;
    if (visible.length === 0) {
      return;
    }

    const from = Math.max(1, Math.min(this.rangeFrom, visible.length));
    const to = Math.max(from, Math.min(this.rangeTo, visible.length));

    const rangeIds = new Set(
      visible.slice(from - 1, to).map((row) => row.mediaId),
    );
    this.rows.forEach((row) => {
      row.selected = rangeIds.has(row.mediaId) && this._canSelectRow(row);
    });
    this._refreshView();
  }

  setModeForAllSelected(mode: EShakaPackagerPackagingMode): void {
    this.setAllMode = mode;
    this.selectedRows.forEach((row) => {
      row.mode = mode;
    });
  }

  toggleRowSelection(row: PackagerEligibleMediaRow): void {
    if (!this._canSelectRow(row) && !row.selected) {
      return;
    }
    row.selected = !row.selected;
    this._refreshView();
  }

  setRowMode(row: PackagerEligibleMediaRow, mode: EShakaPackagerPackagingMode): void {
    row.mode = mode;
  }

  isRowDisabled(row: PackagerEligibleMediaRow): boolean {
    return row.existingCount > 0 && !this.rescheduleExisting;
  }

  async apply(): Promise<void> {
    if (this.isApplying) {
      return;
    }

    const toSubmit = this.selectedRows.filter(
      (row) => this._canSelectRow(row) || this.rescheduleExisting,
    );

    if (toSubmit.length === 0) {
      this._toastr.warning(
        'Select at least one media row. Enable re-schedule for already-scheduled media.',
        'Packager',
        {},
      );
      return;
    }

    this.isApplying = true;
    this._refreshView();

    try {
      const result =
        await this._mediaTranscoderRepository.createShakaPackagerJobsByMedia(
          toSubmit.map((row) => ({
            mediaId: row.mediaId,
            fullMode: isFullPackagingMode(row.mode),
          })),
          { rescheduleExisting: this.rescheduleExisting },
        );

      const { summary } = result;
      const total =
        summary.created + summary.rescheduled + summary.skipped;

      if (summary.created > 0 || summary.rescheduled > 0) {
        const parts: string[] = [];
        if (summary.created > 0) {
          parts.push(`created ${summary.created}`);
        }
        if (summary.rescheduled > 0) {
          parts.push(`re-scheduled ${summary.rescheduled}`);
        }
        if (summary.skipped > 0) {
          parts.push(`skipped ${summary.skipped}`);
        }
        if (summary.notEligible > 0) {
          parts.push(`${summary.notEligible} not eligible`);
        }
        this._toastr.success(parts.join(', ') + '.', 'Success', {});
        this.completed.emit();
        this._ngxSmartModalService.getModal(MODAL_ID).close();
      } else if (total > 0 || summary.notEligible > 0) {
        this._toastr.info(
          `No new jobs (${summary.skipped} skipped, ${summary.notEligible} not eligible).`,
          'Packager',
          {},
        );
      } else {
        this._toastr.warning('No packager jobs were created.', 'Packager', {});
      }
    } catch {
      this._toastr.error('Failed to schedule packager jobs.', 'Error', {});
    } finally {
      this.isApplying = false;
      this._refreshView();
    }
  }

  formatUtc(value: moment.Moment): string {
    return value.format('YYYY-MM-DD HH:mm:ss');
  }

  onFiltersChanged(): void {
    this._refreshView();
  }

  onRescheduleExistingChanged(): void {
    this._refreshView();
  }

  private _parseFilterCutoff(localDatetimeValue: string): moment.Moment | null {
    if (!localDatetimeValue?.trim()) {
      return null;
    }
    // Inputs are labeled UTC: parse components as UTC for comparison with API timestamps
    const parsed = moment.utc(localDatetimeValue);
    return parsed.isValid() ? parsed : null;
  }

  packagerScheduledAt(row: PackagerEligibleMediaRow): moment.Moment | null {
    return row.existingPackagerCreatedAt ?? row.existingPackagerLastUpdateAt;
  }

  private _refreshView(): void {
    this._changeDetectorRef.detectChanges();
  }

  private async _loadEligibleMedia(): Promise<void> {
    this.isLoading = true;
    this._refreshView();
    this.nameFilter = '';
    this.transcodingStartedAfterFilter = '';
    this.packagerScheduledAfterFilter = '';
    this.rescheduleExisting = false;
    this.rangeFrom = 1;
    this.rangeTo = 1;
    this.setAllMode = DEFAULT_SHAKA_PACKAGER_PACKAGING_MODE;

    try {
      const eligible =
        await this._mediaTranscoderRepository.getShakaPackagerEligibleMedia();
      this.rows = eligible.map((item) => ({
        ...item,
        selected: false,
        mode:
          item.existingCount > 0 && item.existingFullMode === false
            ? EShakaPackagerPackagingMode.MetadataOnly
            : DEFAULT_SHAKA_PACKAGER_PACKAGING_MODE,
      }));
      this.rangeTo = Math.max(1, this.rows.length);
    } catch {
      this.rows = [];
      this._toastr.error('Failed to load eligible media.', 'Error', {});
    } finally {
      this.isLoading = false;
      this._refreshView();
    }
  }

  private _canSelectRow(row: PackagerEligibleMediaRow): boolean {
    return row.existingCount === 0 || this.rescheduleExisting;
  }
}
